import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { getFormattedTime } from "common/utils/getFormattedTime";
import { differenceInDays } from "date-fns";
import {
  DIARY_ENTRY_REJECTION_PERIOD_DAYS,
  DiaryEntry,
  useFarmerImageUpload,
  useField,
} from "model";
import { useMemo } from "react";
import { useAppStore } from "stores";
import DiaryEntryConfirmDialog from "./DiaryEntryConfirmDialog";
import { DiaryEntryImage } from "./DiaryEntryImage";

interface Props {
  diaryEntry: DiaryEntry;
  openDiaryEntryDialogGuid: string | null;
  setOpenDiaryEntryDialogGuid: (newValue: string | null) => void;
}

export default function DiaryEntryDialog({
  diaryEntry,
  openDiaryEntryDialogGuid,
  setOpenDiaryEntryDialogGuid,
}: Props) {
  // Always call all hooks first, in the same order every render
  const { t } = useTranslation();
  const locale = useAppStore((state) => state.locale);

  // Use React Query hook for farmer image upload
  const { data: farmerImageUpload } = useFarmerImageUpload(
    diaryEntry.farmerImageUploadId
  );

  // Get the field to extract farmId
  const { data: field } = useField(diaryEntry.fieldGuid);
  const currentFarmId = useAppStore((state) => state.currentFarmId);

  // Compute derived values after all hooks
  const farmId = field?.farm || currentFarmId || "";
  const formattedTime = getFormattedTime(diaryEntry.entryDate, locale);
  const canEdit = useMemo(() => {
    return (
      differenceInDays(
        Date.now(),
        diaryEntry.creationDate ? new Date(diaryEntry.creationDate) : new Date()
      ) <= DIARY_ENTRY_REJECTION_PERIOD_DAYS
    );
  }, [diaryEntry.creationDate]);

  return (
    <Dialog
      fullScreen={false}
      open={openDiaryEntryDialogGuid === diaryEntry.guid}
      onClose={() => {
        setOpenDiaryEntryDialogGuid(null);
      }}
    >
      <DialogTitle>
        <IconButton
          onClick={() => {
            setOpenDiaryEntryDialogGuid(null);
          }}
        >
          <ArrowBackIosIcon />
        </IconButton>
        {t("diaryEntry.showImage.title", {
          diaryEntryDate: formattedTime,
        })}
      </DialogTitle>
      <DialogContent style={{ maxWidth: "300px" }}>
        <DiaryEntryImage
          farmerImageUpload={farmerImageUpload}
          style={{ maxWidth: "300px", width: "100%", height: "auto" }}
          onImageClick={function (): void {
            const x = 1;
          }}
        />
        <Typography>{diaryEntry.diaryEntryText}</Typography>
      </DialogContent>
      <DialogActions>
        <DiaryEntryConfirmDialog
          canEdit={canEdit}
          diaryEntry={diaryEntry}
          setOpenDiaryEntryDialogGuid={setOpenDiaryEntryDialogGuid}
          farmId={farmId}
        ></DiaryEntryConfirmDialog>
      </DialogActions>
    </Dialog>
  );
}
